import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ScrollToTopButton from './components/ScrollToTopButton';
import WhatsAppButton from './components/WhatsAppButton';
import Cart from './components/Cart';
import WelcomePopup from './components/WelcomePopup';
import Catalog from './components/Catalog';
import BrandCarousel from './components/BrandCarousel';
import './components/BrandCarousel.css';
import ClientCarousel from './components/ClientCarousel';
import InstitutionalCarousel from './components/InstitutionalCarousel';
import './styles/responsive-images.css';
import { Product, CartItem } from './types';
import { Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { setupSupabaseListener } from './lib/supabase';
import { initGA, setupScrollTracking, setupLinkTracking } from './lib/analytics';
import { IMAGES } from './config/constants';
import FeaturedProducts from './components/FeaturedProducts';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import { CTA_CARDS } from './config/featureCards';
import LoadingScreen from './components/LoadingScreen';
import LoadingOverlay from './components/LoadingOverlay';
import StatsSection from './components/StatsSection';
import PPECategoryVisualizer from './components/PPECategoryVisualizer';
import CtaCards from './components/CtaCards';
import { getAllPpeVisualizerImageUrls, getAllEPPCategoryImageUrls, preloadImages } from './utils/imageUtils';
import { useFirstScrollPreload } from './hooks/useFirstScrollPreload';

// Code Splitting (Lazy Loading) para rutas secundarias
const ProductDetailWrapper = lazy(() => import('./components/ProductDetailWrapper'));
const UserRegistration = lazy(() => import('./components/UserRegistration'));
const Nosotros = lazy(() => import('./components/Nosotros'));
const OrderDetail = lazy(() => import('./components/OrderDetail'));
const AdminRoutes = lazy(() => import('./routes/AdminRoutes'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const Orders = lazy(() => import('./components/Orders'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));

// Instancia optimizada de QueryClient para evitar re-consultas innecesarias en Supabase Free Tier
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 15 * 60 * 1000, // 15 minutos en caché
      gcTime: 30 * 60 * 1000,    // 30 minutos de persistencia en memoria
      retry: 1
    },
  },
});

const PasswordRecoveryModal = () => {
  const { isPasswordRecovery, setIsPasswordRecovery } = useAuth();
  if (!isPasswordRecovery) return null;
  return (
    <Suspense fallback={<LoadingOverlay />}>
      <ResetPassword isModal={true} onClose={() => setIsPasswordRecovery(false)} />
    </Suspense>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const [isInitialSiteLoadComplete, setIsInitialSiteLoadComplete] = useState(false);
  const isHomePage = location.pathname === '/';

  // Configurar el listener de Supabase en tiempo real dentro del ciclo de vida de React
  useEffect(() => {
    const cleanup = setupSupabaseListener(queryClient);
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, []);

  // Hook para precarga de imágenes EPP en el primer scroll
  const { hasScrolled, isPreloading, preloadComplete, preloadError } = useFirstScrollPreload({
    threshold: 100,
    enabled: isHomePage && isInitialSiteLoadComplete
  });

  useEffect(() => {
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon) {
      favicon.setAttribute('href', IMAGES.FAVICON);
    }
  }, []);

  useEffect(() => {
    if (isInitialSiteLoadComplete) {
      const eppUrls = getAllEPPCategoryImageUrls();
      const industryUrls = getAllPpeVisualizerImageUrls();
      const allUrls = [...eppUrls, ...industryUrls];
      if (allUrls.length > 0) {
        preloadImages(allUrls);
      }
    }
  }, [isInitialSiteLoadComplete]);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Inicializar Google Analytics y tracking
    initGA();
    setupScrollTracking();
    setupLinkTracking();
  }, []);

  const handleAddToCart = (product: Product, quantity: number) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item._id === product.id);
      if (existing) {
        return prev.map((item) =>
          item._id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { 
        _id: product.id,
        name: product.name,
        price: product.price,
        quantity: quantity,
        brand: product.brand,
        category: product.category,
        image_url: product.image_url
      }];
    });
  };

  const handleUpdateQuantity = useCallback((id: string, quantity: number) => {
    setCartItems((prev) => {
      return quantity === 0
        ? prev.filter((item) => item._id !== id)
        : prev.map((item) => (item._id === id ? { ...item, quantity } : item));
    });
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setCartItems((prev) => prev.filter((item) => item._id !== id));
  }, []);

  const handleWelcomeSubmit = (
    data: { companyType: string; industry: string }
  ) => {
    console.log('Welcome data:', data);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PasswordRecoveryModal />
        {isHomePage && isLoading ? (
          <LoadingScreen onLoadingComplete={() => {
            setIsLoading(false);
            setIsInitialSiteLoadComplete(true);
          }} />
        ) : (
          <div className="app flex flex-col min-h-screen">
            <Navbar
              onCartClick={() => setIsCartOpen(true)}
              cartItemsCount={cartItems.length}
              cartItems={cartItems}
              onUpdateQuantity={(id, quantity) => handleUpdateQuantity(id, quantity)}
              onRemoveItem={(id) => handleRemoveItem(id)}
            />
            <main className="flex-grow">
              <Suspense fallback={<LoadingOverlay />}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <>
                        <Hero />
                        <InstitutionalCarousel />
                        <FeaturedProducts 
                          onAddToCart={handleAddToCart} 
                          isAuthenticated={isAuthenticated} 
                        />
                        <BrandCarousel />
                        <div className="w-full">
                          <PPECategoryVisualizer />
                        </div>
                        <StatsSection />
                        <CtaCards cards={CTA_CARDS} />
                        <ClientCarousel />
                      </>
                    }
                  />
                  <Route
                    path="/catalog"
                    element={<Catalog onAddToCart={handleAddToCart} />}
                  />
                  <Route 
                    path="/product/:id" 
                    element={<ProductDetailWrapper onAddToCart={handleAddToCart} />} 
                  />
                  <Route path="/register" element={<UserRegistration />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/nosotros" element={<Nosotros />} />
                  <Route 
                    path="/order-detail" 
                    element={<OrderDetail items={cartItems} />} 
                  />
                  <Route element={<ProtectedRoute />}>
                    <Route path="/admin/*" element={<AdminRoutes />} />
                    <Route path="/perfil" element={<UserProfile />} />
                    <Route path="/pedidos" element={<Orders />} />
                  </Route>
                </Routes>
              </Suspense>
            </main>
            <Footer />
            <Cart
              isOpen={isCartOpen}
              onClose={() => setIsCartOpen(false)}
              items={cartItems}
              onUpdateQuantity={(id: string, quantity: number) => handleUpdateQuantity(id, quantity)}
              onRemoveItem={(id: string) => handleRemoveItem(id)}
            />
            <WelcomePopup
              isOpen={showWelcome}
              onClose={() => setShowWelcome(false)}
              onSubmit={handleWelcomeSubmit}
            />
            <ScrollToTopButton isCartOpen={isCartOpen} />
            <WhatsAppButton isCartOpen={isCartOpen} />
            <ToastContainer
              position="bottom-center"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              style={{ marginBottom: '4rem' }}
            />
          </div>
        )}
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
